export interface AiProcessingStats {
  total: number;
  processing: number;
  completed: number;
  failed: number;
  percentage: number;
}

export type AuditProcessingStepStatus =
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED';

// Ordered per-step progress, populated by the backend for AUDIT items only
// (assetType AUDIT). Built from the durable CAPA workflow record + best-effort
// Redis sub-step progress. `key` ∈ CAPA_WORKFLOW_TYPES. Other asset types omit
// it. See AI_PROCESSING_QUEUE.md "Summary endpoint `steps`".
export interface AuditProcessingStep {
  key: string;
  status: AuditProcessingStepStatus;
  percent: number;
  messageKey: string | null;
  // ISO timestamp set once the user has reviewed this step; null/undefined until
  // then. A reviewable stage flips from REVIEW to COMPLETED once every present
  // member step carries a non-null `reviewedAt`.
  reviewedAt?: string | null;
}

/**
 * `metadata.capImport` on a CAP-only (CAP-import) queue row: where the import the
 * workflow was started for belongs. Written by `POST /capa/process` for every
 * CAP-only flow — `{}` when the caller supplied no context, so the marker itself
 * is never ambiguous. Absent on rows written before that metadata existed and on
 * CAP workflows dispatched by the backend Dropbox file-accept flow.
 *
 * It lives inside `AiProcessingItem.metadata`, which is typed as flat strings but
 * is really a JSONB bag with nested objects (`file`, `error`, `capImport`), so
 * readers cast through `unknown` — see `auditStages.isCapImportContextMissing`.
 */
export interface AiProcessingCapImportContext {
  sourceAuditId?: string;
  companyId?: string;
  capId?: string;
}

export interface AiProcessingItem {
  id: string;
  assetId: string;
  assetType: string;
  assetName: string;
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED';
  percentage: number;
  startedAt: string;
  completedAt: string | null;
  metadata: Record<string, string>;
  steps?: AuditProcessingStep[] | null;
}

export interface AiProcessingSummary {
  stats: AiProcessingStats;
  items: AiProcessingItem[];
}

export enum FileClassificationType {
  CERTIFICATE = 'CERTIFICATE',
  AUDIT = 'AUDIT',
  DOCUMENT = 'DOCUMENT',
  OTHER = 'OTHER',
}

export const FILE_CLASSIFICATION_OPTIONS = [
  { key: FileClassificationType.AUDIT, text: 'Audit' },
  { key: FileClassificationType.CERTIFICATE, text: 'Certificate' },
  { key: FileClassificationType.DOCUMENT, text: 'Document' },
  { key: FileClassificationType.OTHER, text: 'Other' },
];

export interface ClassifyResultClassification {
  type: string;
  confidence: number;
  reasoning: string;
}

export interface ClassifyResultItem {
  fileId: string;
  aiProcessingJobId: string;
  status: 'completed' | 'processing' | 'pending' | 'failed';
  classification: ClassifyResultClassification | null;
  error: string | null;
}

export interface ClassifyResultsResponse {
  results: ClassifyResultItem[];
}

export interface CompanyDocumentProcess {
  id: string;
  fileId: string;
  status: string;
  percentage: number;
  workflows: string[];
  finishedWorkflows: string[];
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string | null;
}
